import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { sanitizeRedirect } from "@relais/shared";
import { supabase } from "../lib/supabase.js";
import { useSession } from "../lib/auth.js";

export const Route = createFileRoute("/login")({
  // `redirect` n'est déclaré que pour que le garde racine puisse l'écrire de
  // façon typée ; le nettoyage qui fait foi est celui du composant
  // (cf. sanitizeRedirect : la validation de route ne filtre pas).
  validateSearch: (
    search: Record<string, unknown>,
  ): { reset?: number; redirect?: string } => ({
    ...(search.reset === "1" || search.reset === 1 ? { reset: 1 } : {}),
    ...(typeof search.redirect === "string"
      ? { redirect: search.redirect }
      : {}),
  }),
  component: LoginPage,
});

const inputClass =
  "mt-1 w-full rounded-md border px-3 py-2 bg-background";

function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });
  const reset = search.reset;
  // Destination d'origine, posée par le garde racine quand la session a
  // expiré sur un lien profond.
  const redirect = sanitizeRedirect(search.redirect);
  const session = useSession();
  const [mode, setMode] = useState<"login" | "forgot" | "sent">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Session déjà valide (onglet resté ouvert, retour arrière vers /login) :
  // on repart aussitôt. `href` plutôt que `to` : la destination est une chaîne
  // arbitraire validée à l'entrée, pas une route typée.
  useEffect(() => {
    if (session.status !== "authed") return;
    navigate({ href: redirect ?? "/", replace: true });
  }, [session.status, redirect, navigate]);

  async function onLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      setError(error.message);
      return;
    }
    navigate({ href: redirect ?? "/", replace: true });
  }

  async function onForgot(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      // Anti-énumération : quel que soit le résultat (succès OU erreur), on affiche
      // le même message neutre et on ne révèle jamais si le compte existe.
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset`,
      });
    } finally {
      setLoading(false);
      setMode("sent");
    }
  }

  return (
    <div className="max-w-sm mx-auto mt-8">
      {reset && (
        <p className="mb-4 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-foreground">
          Mot de passe changé. Reconnectez-vous.
        </p>
      )}

      {!reset && redirect && mode === "login" && (
        <p className="mb-4 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          Votre session a expiré. Connectez-vous pour ouvrir la page demandée.
        </p>
      )}

      {mode === "sent" ? (
        <>
          <h2 className="text-xl font-semibold mb-4">Vérifiez votre boîte mail</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Si un compte existe pour cette adresse, un email de réinitialisation
            vous a été envoyé.
          </p>
          <button
            onClick={() => { setMode("login"); setError(null); }}
            className="text-sm text-primary underline"
          >
            Retour à la connexion
          </button>
        </>
      ) : mode === "forgot" ? (
        <>
          <h2 className="text-xl font-semibold mb-6">Mot de passe oublié</h2>
          <form onSubmit={onForgot} className="space-y-4">
            <label className="block">
              <span className="text-sm text-muted-foreground">Email</span>
              <input
                type="email" required autoFocus value={email}
                onChange={(e) => setEmail(e.target.value)} className={inputClass}
              />
            </label>
            <button
              type="submit" disabled={loading}
              className="w-full rounded-md bg-primary text-primary-foreground py-2 disabled:opacity-50"
            >
              {loading ? "…" : "Envoyer le lien"}
            </button>
          </form>
          <button
            disabled={loading}
            onClick={() => { setMode("login"); setError(null); }}
            className="mt-4 text-sm text-muted-foreground underline disabled:opacity-50"
          >
            Retour à la connexion
          </button>
        </>
      ) : (
        <>
          <h2 className="text-xl font-semibold mb-6">Connexion</h2>
          <form onSubmit={onLogin} className="space-y-4">
            <label className="block">
              <span className="text-sm text-muted-foreground">Email</span>
              <input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} className={inputClass}
              />
            </label>
            <label className="block">
              <span className="text-sm text-muted-foreground">Mot de passe</span>
              <input
                type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} className={inputClass}
              />
            </label>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <button
              type="submit" disabled={loading}
              className="w-full rounded-md bg-primary text-primary-foreground py-2 disabled:opacity-50"
            >
              {loading ? "…" : "Se connecter"}
            </button>
          </form>
          <button
            onClick={() => { setMode("forgot"); setError(null); }}
            className="mt-4 text-sm text-muted-foreground underline"
          >
            Mot de passe oublié ?
          </button>
        </>
      )}
    </div>
  );
}
